import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { cn } from '@/lib/utils';
import { linkifyCitationMarkdown } from '@/features/chat/utils/linkifyCitationMarkdown';
import type { RAGCitation } from '@/services/api';
import type { Components } from 'react-markdown';

interface MarkdownMessageProps {
  content: string;
  isUser?: boolean;
  citations?: RAGCitation[] | null;
}

// rehypeRaw parses raw HTML embedded in markdown; sanitizing right after is
// what keeps that from being an XSS vector. Rendered content includes
// documents synced from the external KB repo and crawled sources — never
// treat it as trusted. The default schema already permits the common
// document elements (tables, images, headings) while stripping script,
// event handlers, and javascript: URLs.
const sanitizeSchema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    // Citation links injected by linkifyCitationMarkdown carry target/rel;
    // the custom `a` renderer below re-applies them anyway, but keeping them
    // through sanitize avoids a hast/props mismatch.
    a: [...(defaultSchema.attributes?.a ?? []), 'target', 'rel'],
  },
};

const MarkdownMessage: React.FC<MarkdownMessageProps> = ({
  content,
  isUser = false,
  citations,
}) => {
  const renderedContent = useMemo(() => {
    if (isUser || !citations?.length) return content;
    return linkifyCitationMarkdown(content, citations);
  }, [content, citations, isUser]);

  return (
    <div
      className={cn(
        "prose prose-sm max-w-none",
        isUser ? "prose-invert" : "",
        "[&_p]:my-0 [&_p]:mb-2 [&_p:last-child]:mb-0",
        "[&_h1]:mt-4 [&_h1]:mb-2 [&_h1]:font-semibold [&_h1]:text-3xl",
        "[&_h2]:mt-4 [&_h2]:mb-2 [&_h2]:font-semibold [&_h2]:text-2xl",
        "[&_h3]:mt-4 [&_h3]:mb-2 [&_h3]:font-semibold [&_h3]:text-xl",
        "[&_ul]:mt-1 [&_ul]:mb-2 [&_ul]:pl-6",
        "[&_ol]:mt-1 [&_ol]:mb-2 [&_ol]:pl-6",
        "[&_li]:mb-1",
        "[&_blockquote]:border-l-4 [&_blockquote]:pl-4 [&_blockquote]:ml-0 [&_blockquote]:my-2 [&_blockquote]:italic [&_blockquote]:opacity-90",
        isUser 
          ? "[&_blockquote]:border-white/50" 
          : "[&_blockquote]:border-primary",
        "[&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-sm [&_code]:font-mono",
        isUser
          ? "[&_code]:bg-black/20"
          : "[&_code]:bg-black/5",
        "[&_pre]:m-0 [&_pre]:my-2 [&_pre_code]:p-0 [&_pre_code]:bg-transparent",
        "[&_table]:border-collapse [&_table]:w-full [&_table]:my-2",
        "[&_th]:border [&_th]:p-2 [&_th]:text-left [&_th]:font-semibold",
        "[&_td]:border [&_td]:p-2 [&_td]:text-left",
        isUser
          ? "[&_th]:border-white/30 [&_th]:bg-black/20 [&_td]:border-white/30"
          : "[&_th]:border-border [&_th]:bg-muted/50 [&_td]:border-border",
        "[&_hr]:border-0 [&_hr]:border-t [&_hr]:my-4",
        isUser ? "[&_hr]:border-white/30" : "[&_hr]:border-border",
        "[&_a]:underline",
        isUser ? "[&_a]:text-white/90" : "[&_a]:text-primary"
      )}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizeSchema]]}
        components={{
          code({ className, children, ...props }) {
            const match = /language-(\w+)/.exec(className || '');
            const language = match ? match[1] : '';
            const isInline = !className && !language;
            
            return !isInline && language ? (
              <SyntaxHighlighter
                style={vscDarkPlus as any}
                language={language}
                PreTag="div"
                customStyle={{
                  margin: 0,
                  borderRadius: '4px',
                  fontSize: '0.9em',
                }}
              >
                {String(children).replace(/\n$/, '')}
              </SyntaxHighlighter>
            ) : (
              <code className={className} {...props}>
                {children}
              </code>
            );
          },
          a({ children, href, ...props }) {
            return (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:opacity-80 transition-opacity"
                {...props}
              >
                {children}
              </a>
            );
          },
          p({ children, ...props }) {
            return (
              <p {...props}>
                {children}
              </p>
            );
          },
        } as Components}
      >
        {renderedContent}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownMessage;
